import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import Styles from './index.module.scss';
import { DocsStore } from '../../models';
import DocContent from '@/pages/docs/component/DocContent';
import { useUrlQuery } from '@/utils/hooks/common';
import Header from './header';
import SEO from '@/component/SEO';
import { getSEOConfig } from '@/pages/docs/config/seo';

interface DocContentData {
  type: string;
  content: string;
  title: string;
}

function Preview() {
  const docsInfoStore = useContext(DocsStore);
  const {
    size,
    entity_id: entityIdParam,
  } = useUrlQuery();
  const [error, setError] = useState('');
  const [allDocContents, setAllDocContents] = useState<{ [lang: string]: DocContentData }>({});
  const [selectedLang, setSelectedLang] = useState<string>('eng');
  const [contentMap, setContentMap] = useState<{ [index: number]: string }>({});
  const [renderedCount, setRenderedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [language, setLanguage] = useState<string>('ENGLISH');
  const [articleTitle, setArticleTitle] = useState<string>('');
  const seoConfig = getSEOConfig(entityIdParam || 'default');

  useEffect(() => {
    const maxRetries = 10;

    const fetchWithRetry = async (url: string, retryCount = 0): Promise<any> => {
      try {
        const response = await fetch(url);

        if (response.status === 504) {
          if (retryCount < maxRetries) {
            setError(`Request timed out, retrying (${retryCount + 1}/${maxRetries})...`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return fetchWithRetry(url, retryCount + 1);
          }
          throw new Error('Request timed out, maximum retry attempts reached');
        }

        const result = await response.json();

        if (result.code !== 0) {
          throw new Error(result.msg || 'Failed to fetch content');
        }

        return result.data;
      } catch (err) {
        if (retryCount < maxRetries) {
          setError(`Request failed, retrying (${retryCount + 1}/${maxRetries})...`);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return fetchWithRetry(url, retryCount + 1);
        }
        throw err;
      }
    };

    const fetchContent = async (): Promise<void> => {
      try {
        docsInfoStore.docContentStatus = 'loading';
        setError('');
        setContentMap({});
        setRenderedCount(0);
        setTotalCount(0);

        if (!entityIdParam) {
          throw new Error('Please provide entity_id parameter');
        }

        const idListUrl = `/marketing_api/api/cms/client/node/get_from_contentful/?language=${language}&entity_id=${entityIdParam}`;
        const idListResponse = await fetchWithRetry(idListUrl);

        let isIdListFormat = false;
        let idListData: any[] = [];

        try {
          if (idListResponse.content && typeof idListResponse.content === 'string') {
            const jsonStr = idListResponse.content.replace(/'/g, '"');
            idListData = JSON.parse(jsonStr);
          } else {
            idListData = idListResponse;
          }

          if (Array.isArray(idListData)) {
            const isValidFormat = idListData.every((item: any) => item.sys
              && item.sys.type === 'Link'
              && item.sys.linkType === 'Entry'
              && item.sys.id);
            isIdListFormat = isValidFormat;
          }
        } catch (parseErr) {
          isIdListFormat = false;
        }

        setArticleTitle(idListResponse.title || '');

        if (isIdListFormat) {
          setTotalCount(idListData.length);

          const contentPromises = idListData.map((item: any, index: number) => {
            const contentUrl = `/marketing_api/api/cms/client/node/get_from_contentful/?language=${language}&entity_id=${item.sys.id}`;

            return fetchWithRetry(contentUrl).then((contentData: any) => {
              setContentMap((prev) => ({
                ...prev,
                [index]: contentData.content,
              }));
              return contentData;
            });
          });

          Promise.all(contentPromises).then(() => {
            docsInfoStore.docContentStatus = 'success';
          });
        } else {
          const directContent = idListResponse.content || idListResponse;
          const newAllDocContents: { [lang: string]: DocContentData } = {};

          newAllDocContents.eng = {
            type: 'MARKDOWN',
            content: directContent,
            title: articleTitle,
          };

          setAllDocContents(newAllDocContents);

          if (newAllDocContents.eng) {
            setSelectedLang('eng');
            docsInfoStore.docContent = newAllDocContents.eng;
          }

          docsInfoStore.docContentStatus = 'success';
        }

        window.parent.postMessage({ entityId: entityIdParam }, '*');

        setError('');
      } catch (err) {
        const fetchError = err as Error;
        setError(fetchError.message || 'Failed to fetch content');
        docsInfoStore.docContentStatus = 'success';
      }
    };

    fetchContent();
  }, [entityIdParam, language]);

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
  };

  useEffect(() => {
    let newRenderedCount = renderedCount;
    const orderedContents: string[] = [];

    for (let i = 0; i < totalCount; i++) {
      if (contentMap[i] !== undefined) {
        orderedContents.push(contentMap[i]);
        newRenderedCount = i + 1;
      } else {
        break;
      }
    }

    if (newRenderedCount > renderedCount && orderedContents.length > 0) {
      setRenderedCount(newRenderedCount);

      const newAllDocContents: { [lang: string]: DocContentData } = {};
      const combinedContent = orderedContents.join('\n');

      newAllDocContents.eng = {
        type: 'MARKDOWN',
        content: combinedContent,
        title: articleTitle,
      };

      setAllDocContents(newAllDocContents);

      if (newAllDocContents.eng) {
        setSelectedLang('eng');
        docsInfoStore.docContent = newAllDocContents.eng;
      }
    }
  }, [contentMap, renderedCount, totalCount, articleTitle]);

  const handleLangChange = (lang: string) => {
    if (allDocContents[lang]) {
      setSelectedLang(lang);
      docsInfoStore.docContent = allDocContents[lang];
      docsInfoStore.language = lang;
    }
  };

  if (error) {
    return (
      <div className={Styles.error}>
        <h3>Error</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
      />
      <div className={`${Styles.container} ${size}`}>
        <Header onLanguageChange={handleLanguageChange} />
        <div className={Styles.contentWrapper}>
          <div className={Styles.docContentContainer}>
            <DocContent />
          </div>
        </div>
      </div>
    </>
  );
}

export default React.memo(observer(Preview));
